<template>
  <div class="picky-cat-diet__carousel" :class="{'_circle': activeSlide !== 0}">
    <div class="picky-cat-diet__carousel-inner">
      <div class="picky-cat-diet__carousel-plates _g-hidden-mobile">
        <div class="picky-cat-diet__item-plate _dry" :class="{'_hidden': activeSlide === 2}">
          <img src="/themes/custom/purina_one/purina-one-new_cat/src/img/picky-cat/plate-dry.png" alt="">
        </div>
        <div class="picky-cat-diet__item-plate _wet" :class="{'_hidden': activeSlide === 1}">
          <img src="/themes/custom/purina_one/purina-one-new_cat/src/img/picky-cat/plate-wet.png" alt="">
        </div>
      </div>
      <swiper
          :modules="modules"
          :direction="'vertical'"
          :effect="'fade'"
          :fadeEffect="{crossFade: true}"
          :slidesPerView="'auto'"
          :navigation="false"
          :speed="1000"
          :parallax="true"
          @swiper="setSwiperRef"
          @slideChange="onSlideChange"
          @slideChangeTransitionEnd="onSlideTransitionEnd"
      >
        <slot />
      </swiper>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { EffectFade, Mousewheel, Parallax } from 'swiper';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const modules = [EffectFade, Mousewheel, Parallax];

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  features: {
    type: Array,
    default: []
  }
});
let swiperRef = ref(null);

let activeSlide = ref(0);

const setSwiperRef = (swiper) => {
  swiperRef.value = swiper;
};

let animating = ref();

let windowWidth = ref(window.innerWidth);

let intentObserver = ScrollTrigger.observe({
  type: "wheel,touch,scroll",
  onUp: () => !animating.value && goToSlide(activeSlide.value + 1, true),
  onDown: () => !animating.value && goToSlide(activeSlide.value - 1, false),
  wheelSpeed: -1,
  tolerance: 30,
  preventDefault: true,
  onPress: self => {
    ScrollTrigger.isTouch && self.event.preventDefault()
  }
})

let preventScroll = ScrollTrigger.observe({
  preventDefault: true,
  type: "wheel",
  allowClicks: true,
  onEnable: self => {
    return self.savedScroll = self.scrollY()
  },
  onChangeY: self => {
    self.scrollY(self.savedScroll)
  }
});

intentObserver.disable();
preventScroll.disable();

onMounted(() => {
  window.addEventListener('resize', onResize);
  if (windowWidth.value > 768) {
    console.log(windowWidth.value);
    ScrollTrigger.create({
      trigger: '.picky-cat-diet__carousel',
      pin: true,
      anticipatePin: true,
      start: 'top top',
      end: '+=30%',
      onEnter: (self) => {
        if (preventScroll.isEnabled === false) {
          self.scroll(self.start);
          preventScroll.enable();
          intentObserver.enable();

          goToSlide(activeSlide.value + 1, true);
        }
      },
      onEnterBack: (self) => {
        if (preventScroll.isEnabled === false) {
          self.scroll(self.start);
          preventScroll.enable();
          intentObserver.enable();

          goToSlide(activeSlide.value - 1, false);
        }
      }
    });

    ScrollTrigger.config({
      ignoreMobileResize: true
    });
  } else {
    swiperRef.value.destroy();

    gsap.utils.toArray('.picky-cat-diet__carousel .swiper-slide').forEach(section => {
      const fade_elems = section.querySelectorAll('.picky-cat-diet__item-plate._hidden');
      const float_bottom_elems = section.querySelectorAll('.picky-cat-diet__item-feature');
      const fly_left_elems = section.querySelectorAll('.picky-cat-diet__item-list._left');
      const fly_right_elems = section.querySelectorAll('.picky-cat-diet__item-list._right');

      gsap.set(fade_elems, {
        opacity: 1,
        duration: 2,
        ease: 'power3.out',
        overwrite: 'auto',
      });

      ScrollTrigger.create({
        trigger: section,
        start: 'top',
        end: 'bottom',
        onEnter: () => gsap.to(fade_elems, {
          opacity: 0.16,
          stagger: 0.2,
        }),
        onLeave: () => gsap.to(fade_elems, {
          opacity: 1,
          stagger: 0.2,
        }),
      });

      gsap.set(float_bottom_elems, {
        opacity: 0,
        duration: 4,
        y: 100,
        ease: 'none',
        overwrite: 'auto',
      });

      ScrollTrigger.create({
        trigger: section,
        start: 'top',
        end: 'bottom',
        onEnter: () => gsap.to(float_bottom_elems, {
          opacity: 1,
          y: 0,
          stagger: 0.2,
        }),
        onLeave: () => gsap.to(float_bottom_elems, {
          opacity: 1,
          y: 0,
          stagger: 0.2,
        }),
      });

      gsap.set(fly_left_elems, {
        opacity: 0,
        duration: 6,
        x: -100,
        ease: 'power2.out',
        overwrite: 'auto',
      });

      ScrollTrigger.create({
        trigger: section,
        start: 'top',
        end: 'bottom',
        onEnter: () => gsap.to(fly_left_elems, {
          opacity: 1,
          x: 0,
          stagger: 0.2,
        }),
        onLeave: () => gsap.to(fly_left_elems, {
          opacity: 1,
          x: 0,
          stagger: 0.2,
        }),
      });

      gsap.set(fly_right_elems, {
        opacity: 0,
        duration: 6,
        x: 100,
        ease: 'power2.out',
        overwrite: 'auto',
      });

      ScrollTrigger.create({
        trigger: section,
        start: 'top',
        end: 'bottom',
        onEnter: () => gsap.to(fly_right_elems, {
          opacity: 1,
          x: 0,
          stagger: 0.2,
        }),
        onLeave: () => gsap.to(fly_right_elems, {
          opacity: 1,
          x: 0,
          stagger: 0.2,
        }),
      });
    });
  }
});

onUnmounted(() => {
  window.addEventListener('resize', onResize);
});

const onResize = () => {
  windowWidth.value = window.innerWidth;
}

const onSlideChange = () => {
  activeSlide.value = swiperRef.value.activeIndex;
}

const onSlideTransitionEnd = () => {
  animating.value = false;
}

const goToSlide = (index, isScrollingDown) => {
  animating.value = true;
  const numPanels = swiperRef.value.slides.length;
  if ((index === numPanels && isScrollingDown) || (index === -1 && !isScrollingDown)) {
    intentObserver.disable();
    preventScroll.disable();

    animating.value = false;
    preventScroll.scrollY(preventScroll.scrollY() + (index === numPanels ? 1 : -1));
    return;
  }
  swiperRef.value.slideTo(index);
}
</script>